import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, MapPin, Clock, AlertCircle, X, Loader } from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';

const AvailableOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [acceptingOrder, setAcceptingOrder] = useState(false);

  // Fetch orders
  useEffect(() => {
    fetchOrders();
  }, []);

  const token = localStorage.getItem('driverToken');

  const fetchOrders = async () => {
    try {
      const response = await axios.get('https://afric-fresh-backend.onrender.com/driver/orders/pending-payment'          ,{
        headers: {
          Authorization: `Bearer ${token}`
        }});
      setOrders(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
    }
  };

  const handleAcceptOrder = async () => {
    if (!selectedOrder) return;
  
    setAcceptingOrder(true);
    try {
      const driverData = JSON.parse(localStorage.getItem('driverData'));
      const token = localStorage.getItem('driverToken');
  
      const driverDetails = {
        driverId: driverData.id,
        driverName: driverData.name,
        driverEmail: driverData.email,
        driverPhone: driverData.phone
      };
  
      // First request to accept the delivery
      await axios.post(
        'https://afric-fresh-backend.onrender.com/driver/deliveries/accept',
        {
          orderId: selectedOrder._id,
          ...driverDetails
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
  
      // Second request to update order status
      await axios.patch(
        `https://afric-fresh-backend.onrender.com/driver/orders/${selectedOrder._id}`,
        {
          orderStatus: 'shipped'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
  
      Swal.fire({
        icon: 'success',
        title: 'Order Accepted!',
        text: 'You have successfully accepted this delivery. You can now proceed to deliver the order. Please do well to check your email for more details. Thank you',
        timer: 4000,
        showConfirmButton: false
      });
  
      setIsModalOpen(false);
      fetchOrders(); // Refresh orders list
    } catch (error) {
      console.error('Error accepting order:', error);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.response?.data?.message || 'Something went wrong! Please try again.'
      });
    } finally {
      setAcceptingOrder(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Available Orders</h1>
      
      <motion.div 
        layout
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
      >
        {orders.map((order) => (
          <motion.div
            key={order._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white p-4 md:p-6 rounded-lg shadow-sm"
          >
            <div className="flex items-start space-x-4">
              <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-lg font-semibold text-gray-800">
                  Order #{order.orderNumber}
                </p>
                <div className="flex items-center space-x-2 mt-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <p className="text-sm text-gray-600">
                    {new Date(order.date).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <p className="text-sm text-gray-600">
                    {order.billingAddress.city}, {order.billingAddress.state}
                  </p>
                </div>
                <p className="mt-2 text-sm font-semibold text-gray-700">
                  Total: ${order.total}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedOrder(order);
                setIsModalOpen(true);
              }}
              className="mt-4 w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition"
            >
              View Details
            </button>
          </motion.div>
        ))}
      </motion.div>

      <AnimatePresence>
  {isModalOpen && selectedOrder && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[999]"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-start">
          <h2 className="text-xl font-bold text-gray-800">
            Order Details #{selectedOrder.orderNumber}
          </h2>
          <button
            onClick={() => setIsModalOpen(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold text-gray-700">Billing Address</h3>
              <p className="text-sm text-gray-600">
                {selectedOrder.billingAddress.firstName} {selectedOrder.billingAddress.lastName}<br />
                {selectedOrder.billingAddress.streetHouseApartment}<br />
                {selectedOrder.billingAddress.city}, {selectedOrder.billingAddress.state} {selectedOrder.billingAddress.zipCode}<br />
                {selectedOrder.billingAddress.phone}<br/>
                {selectedOrder.billingAddress.email}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700">Order Summary</h3>
              <p className="text-sm text-gray-600">
                Subtotal: ${selectedOrder.subtotal}<br />
                Shipping: ${selectedOrder.shippingCost}<br />
                <span className="font-semibold">Total: ${selectedOrder.total}</span><br /><br />
                <span className="font-semibold">Shipping Method:</span> {selectedOrder.shippingMethod}<br />
                <span className="font-semibold">Delivery Time:</span> {selectedOrder.deliveryTime || 'Not specified'}
              </p>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-semibold text-gray-700 mb-2">Items</h3>
            <div className="space-y-2">
              {selectedOrder.cartItems.map((item) => (
                <div key={item.id} className="flex items-center space-x-4 p-2 bg-gray-50 rounded">
                  <div>
                  <img src={item.img} className=' w-20' />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-sm text-gray-600">
                      Quantity: {item.quantity} × ${item.price}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleAcceptOrder}
            disabled={acceptingOrder}
            className="mt-6 w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition disabled:bg-blue-300 flex items-center justify-center"
          >
            {acceptingOrder ? (
              <>
                <Loader className="w-5 h-5 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              'Accept Order'
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>
    </div>
  );
};

export default AvailableOrdersPage;